'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useEffect, useRef, useState } from 'react';
import { useClickAway, usePrevious } from 'react-use';
import { useMount } from 'react-use';

import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import cx from 'classnames';
import Script from 'next/script';
import PropTypes from 'prop-types';

import Button from 'components/ui/Button';
import Portal from 'components/ui/Portal';
import Spinner from 'components/ui/Spinner';
import SvgIcon from 'components/ui/SvgIcon';

import styles from './DestiniBuyButton.module.scss';

const DestiniBuyButton = ({ sku, button, disableScroll }) => {
    disableScroll ??= true;

    const [isOpen, setIsOpen] = useState(false);
    const [isLoaded, setLoaded] = useState(false);
    const [scriptRequested, setScriptRequested] = useState(false);
    const [scriptLoaded, setScriptLoaded] = useState(false);

    const prevIsOpen = usePrevious(isOpen);
    const destiniDiv = useRef(null);
    const overlayRef = useRef(null);

    const handleOpen = () => {
        setScriptRequested(true);
        setIsOpen(true);
    };

    const handleClose = () => {
        enableBodyScroll(overlayRef?.current);
        setIsOpen(false);
        setLoaded(false);
    };

    const ref = useRef(null);

    useMount(() => {
        window.addEventListener(
            'message',
            event => {
                if (/^{"eventCategory":"DESTINI_LOAD"/gi.test(event.data)) {
                    setTimeout(() => setLoaded(true), 1000);
                }
            },
            false
        );
    });

    useClickAway(ref, () => {
        enableBodyScroll(overlayRef?.current);
        setIsOpen(false);
    });

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (!window.destini) {
            // Script is still loading; init re-runs once it's ready via scriptLoaded.
            return;
        }

        if (isOpen !== prevIsOpen || scriptLoaded) {
            window.destini?.init('destinidiv');
            window.destini?.loadWidget('default', sku);

            if (disableScroll && overlayRef?.current) {
                disableBodyScroll(overlayRef?.current, {
                    allowTouchMove: el => {
                        while (el && el !== document.body) {
                            if (
                                el.getAttribute('body-scroll-lock-ignore') !==
                                null
                            ) {
                                return true;
                            }

                            el = el.parentElement;
                        }
                    },
                });
                document.body.style.setProperty(
                    'top',
                    `${window.scrollY * -1}px`
                ); // fix for scroll-to-top body-scroll-lock bug: https://github.com/willmcpo/body-scroll-lock/issues/237#issuecomment-954804479
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, prevIsOpen, disableScroll, scriptLoaded]);

    if (!sku || !button) {
        return null;
    }

    return (
        <>
            {scriptRequested && (
                <Script
                    src="//destinilocators.com/tillamook/pdpwidget/install/"
                    strategy="afterInteractive"
                    onLoad={() => setScriptLoaded(true)}
                />
            )}
            {React.cloneElement(button, { onClick: handleOpen })}
            <Portal selector="#__tertiary_portal__">
                {isOpen && (
                    <div
                        ref={overlayRef}
                        className={cx(styles.destiniOverlay, {
                            [styles.destiniOverlayIsOpen]: isOpen,
                        })}
                    >
                        <div
                            className={styles.destiniOverlayInnerWrap}
                            ref={ref}
                        >
                            {!isLoaded && (
                                <div className={styles.loadingOverlay}>
                                    <Spinner />
                                </div>
                            )}
                            <div
                                className={cx(
                                    styles.destiniOverlayBackdrop,
                                    styles.transparent
                                )}
                                onClick={handleClose}
                                role="presentation"
                            />
                            <Button
                                className={styles.destiniOverlayCloseIcon}
                                theme="close"
                                onClick={handleClose}
                            >
                                <SvgIcon type="close" />
                                <span className="sr-only">Close Product</span>
                            </Button>
                            <div
                                className={styles.destiniIframeDiv}
                                ref={destiniDiv}
                                id="destinidiv"
                            ></div>
                        </div>
                    </div>
                )}
            </Portal>
        </>
    );
};

DestiniBuyButton.propTypes = {
    button: PropTypes.element.isRequired,
    sku: PropTypes.array.isRequired,
    disableScroll: PropTypes.bool,
};

export default DestiniBuyButton;
